import querystring from 'querystring';
import io from 'socket.io-client';
import { v4 } from 'uuid';

let socket;
let currentRoom = null;
let isExplicitlyDisconnected = false;

const resolveSocketUrl = () => {
  const envUrl = process.env.REACT_APP_SOCKET_URL;
  if (envUrl && envUrl.trim().length) {
    return envUrl.trim();
  }

  if (
    typeof window !== 'undefined' &&
    window.location.hostname === 'localhost' &&
    window.location.port === '3000'
  ) {
    // When running through CRA dev server we bypass the proxy to avoid ECONNRESET.
    return 'http://localhost:3005';
  }

  return undefined;
};

export const initiateSocket = room => {
  currentRoom = room;
  isExplicitlyDisconnected = false;

  const defaultKey = 'smartbotsWebChatUserId';
  const currentUrl = window.location.href;
  let uId = localStorage.getItem(defaultKey);
  const accessToken = localStorage.getItem('accessToken');

  // Extract botId from URL - try multiple methods
  let botID = null;

  try {
    // Method 1: Try the original parsing method
    botID = JSON.parse(Object.values(querystring.parse(currentUrl))[0] || null)
      ?.config?.botId;
  } catch (error) {
    console.warn('Failed to parse botId from URL using method 1:', error);
  }

  // Method 2: Try to extract from URL path (e.g., /analytics/botId)
  if (!botID) {
    const pathMatch = currentUrl.match(/\/analytics\/([^/?]+)/);
    if (pathMatch) {
      botID = pathMatch[1];
    }
  }

  // Method 3: Try to get from URL parameters
  if (!botID) {
    const urlParams = new URLSearchParams(window.location.search);
    botID = urlParams.get('botId') || urlParams.get('botID');
  }

  // Method 4: Try to get from room parameter if provided
  if (!botID && room) {
    botID = room;
  }

  if (!uId) {
    uId = v4();
    localStorage.setItem(defaultKey, uId);
  }

  if (socket) socket.disconnect();

  const socketUrl = resolveSocketUrl();
  const connectionOptions = {
    auth: {
      accessToken
    },
    query: {
      botID
    },
    forceNew: true,
    reconnection: true,
    reconnectionAttempts: 300,
    reconnectionDelay: 1200
  };

  socket = socketUrl ? io(socketUrl, connectionOptions) : io(connectionOptions);

  socket.on('disconnect', () => {
    localStorage.setItem('disconnected', 'true');
  });

  socket.on('connect', () => {
    const latestToken = localStorage.getItem('accessToken');
    if (latestToken && socket.auth) {
      socket.auth.accessToken = latestToken;
    }

    if (localStorage.getItem('disconnected') === 'true') {
      localStorage.setItem('disconnected', 'false');
    }

    if (currentRoom) {
      socket.emit('join', currentRoom);
    }
  });

  if (socket && currentRoom) {
    socket.emit('join', currentRoom);
  }

  return socket;
};

export const disconnectSocket = () => {
  isExplicitlyDisconnected = true;
  currentRoom = null;
  if (socket) socket.disconnect();
};

export const subscribeToChat = cb => {
  if (!socket) return true;
  socket.off('realtime');
  socket.on('realtime', msg => cb(null, msg));
};

export const onDisconnect = cb => {
  if (!socket) return true;
  socket.off('disconnect', cb);
  socket.on('disconnect', msg => cb(null, msg));
};

export const onConnect = cb => {
  if (!socket) return true;
  socket.off('connect', cb);
  socket.on('connect', msg => cb(null, msg));
};

export const sendMessage = (room, message) => {
  if (socket) socket.emit('chat', { message, room });
};

if (typeof window !== 'undefined') {
  // Re-establish connection when restored from Back-Forward Cache (bfcache)
  window.addEventListener('pageshow', event => {
    if (
      event.persisted &&
      !isExplicitlyDisconnected &&
      socket &&
      !socket.connected
    ) {
      socket.connect();
    }
  });

  // Re-establish connection when agent switches back to the tab from another application
  document.addEventListener('visibilitychange', () => {
    if (
      document.visibilityState === 'visible' &&
      !isExplicitlyDisconnected &&
      socket &&
      !socket.connected
    ) {
      socket.connect();
    }
  });
}

